import React, { useState, type FormEvent } from "react";
import { type ApiAppUser } from "../../api.ts";
import { Button } from "../../components/button.tsx";
import { CopyOnceCallout } from "../../components/copy-once-callout.tsx";
import { ErrorBanner } from "../../components/error-banner.tsx";
import { Input } from "../../components/input.tsx";
import { MaskedSecret } from "../../components/masked-secret.tsx";

const MIN_ACCESS_CODE_LENGTH = 12;

function generateSuggestedAccessCode(): string {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/=+$/, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
}

interface UsersFormProps {
  users: ApiAppUser[];
  onCreate: (email: string, accessCode: string) => Promise<void>;
  onDelete: (userId: string) => Promise<void>;
}

export function UsersForm({ users, onCreate, onDelete }: UsersFormProps) {
  const [newEmail, setNewEmail] = useState("");
  const [newAccessCode, setNewAccessCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<{
    email: string;
    accessCode: string;
  } | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed || newAccessCode.length < MIN_ACCESS_CODE_LENGTH) {
      return;
    }
    setError(null);
    try {
      await onCreate(trimmed, newAccessCode);
      setLastCreated({ email: trimmed, accessCode: newAccessCode });
      setNewEmail("");
      setNewAccessCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleDelete(userId: string) {
    if (!confirm("Delete this user? They will no longer be able to sign in.")) {
      return;
    }
    await onDelete(userId);
  }

  return (
    <>
      <ErrorBanner message={error} className="mb-4" />

      {/* Mobile-only header. Desktop header is rendered inside the grid
          below so its columns share widths with the form/user rows. */}
      <div className="mb-2 flex items-center justify-between sm:hidden">
        <span className="font-mono text-xs uppercase tracking-wider text-muted">
          New mapping
        </span>
        <button
          type="button"
          onClick={() => setNewAccessCode(generateSuggestedAccessCode())}
          className="cursor-pointer font-mono text-xs uppercase tracking-wider text-accent hover:text-accent-strong"
        >
          generate code
        </button>
      </div>

      {/* Single grid: 1-col on mobile (each row's children stack), aligned
          4-col on sm+. Header, form, and user-row wrappers use sm:contents
          so their children become direct grid items on desktop, while
          staying as ordinary flex containers on mobile. */}
      <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-center sm:gap-x-3">
        {/* Desktop header labels — share column widths with form via sm:contents. */}
        <div className="hidden sm:contents">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs uppercase tracking-wider text-muted">
              Access code
            </span>
            <button
              type="button"
              onClick={() => setNewAccessCode(generateSuggestedAccessCode())}
              className="cursor-pointer font-mono text-xs uppercase tracking-wider text-accent hover:text-accent-strong"
            >
              generate
            </button>
          </div>
          <span />
          <span className="font-mono text-xs uppercase tracking-wider text-muted">
            Email
          </span>
          <span />
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-2 sm:contents"
        >
          <Input
            value={newAccessCode}
            onChange={(event) => setNewAccessCode(event.target.value)}
            placeholder="Access code"
            minLength={MIN_ACCESS_CODE_LENGTH}
            required
            mono
          />
          <span className="hidden text-muted sm:flex sm:items-center sm:justify-center">
            →
          </span>
          <Input
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            placeholder="user@example.com"
            required
          />
          <Button type="submit">
            Add
          </Button>
        </form>

        {users.map((user) => (
          <div
            key={user.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t-2 border-faint pt-3 sm:contents sm:border-0 sm:pt-0"
          >
            <MaskedSecret prefix={user.accessCodePrefix} />
            <span className="hidden text-muted sm:flex sm:items-center sm:justify-center">
              →
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs sm:flex-auto">
              {user.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(user.id)}
            >
              Delete
            </Button>
          </div>
        ))}
      </div>

      {lastCreated ? (
        <div className="mt-4">
          <CopyOnceCallout
            message={`Copy the access code for ${lastCreated.email} now — it cannot be recovered.`}
            value={lastCreated.accessCode}
          />
        </div>
      ) : null}
    </>
  );
}

